import { useEffect } from 'react';

// Edit lock for the rundown page.
//
// While on, every click, drag, context menu and keystroke is swallowed before
// any component sees it, so nothing can be edited, dragged or opened by
// accident mid-show. The only exceptions are the controls that run the show:
//   • elements marked data-lock-allow — TAKE, SKIP, BACK and the padlock
//   • the TAKE / BACK / SKIP hotkeys: Space, B and S
//
// One capture-phase listener on window rather than a `locked` prop threaded
// through every view: it runs before React's handlers and before dnd-kit's
// sensors, so a view added later is locked without anyone remembering to wire
// it in. Scrolling is untouched, and pointerup is deliberately not blocked so a
// drag already in flight when the lock engages can still be let go.

const BLOCKED_EVENTS = [
  'pointerdown', 'mousedown', 'click', 'dblclick', 'auxclick', 'contextmenu',
  'dragstart', 'drop', 'paste', 'cut',
];

// Must match the TAKE / BACK / SKIP cases in RundownEditor's key handler.
const LIVE_KEYS = new Set([' ', 'b', 'B', 's', 'S']);

function isAllowed(target) {
  return target instanceof Element && !!target.closest('[data-lock-allow]');
}

function blockEvent(e) {
  if (isAllowed(e.target)) return;
  e.preventDefault();
  e.stopPropagation();
}

function blockKey(e) {
  const plain = !e.ctrlKey && !e.metaKey && !e.altKey;
  if (plain && LIVE_KEYS.has(e.key)) return;
  // No app handler sees the key. Browser shortcuts (reload, zoom, closing a
  // tab) keep their default, though — a page that has wedged mid-show has to
  // stay recoverable, and none of them edit the rundown.
  e.stopPropagation();
  if (plain && !/^F\d+$/.test(e.key)) e.preventDefault();
}

export function useEditLock(locked) {
  useEffect(() => {
    if (!locked) return;

    // Drop focus, so a half-open inline edit cannot keep taking keystrokes and
    // a focused control cannot be activated from the keyboard.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();

    const opts = { capture: true };
    BLOCKED_EVENTS.forEach((type) => window.addEventListener(type, blockEvent, opts));
    window.addEventListener('keydown', blockKey, opts);
    return () => {
      BLOCKED_EVENTS.forEach((type) => window.removeEventListener(type, blockEvent, opts));
      window.removeEventListener('keydown', blockKey, opts);
    };
  }, [locked]);
}
